import { push, replace } from 'connected-react-router';
import { views, FETCH_DATA_PREFIX, FETCH_NAVIGATION_PREFIX, SET_SELECTED_WISHLIST } from '../constants';
import { isWishlistOwner, getMessages, getUserState } from '../selectors';
import getRequest from '../utils/getRequest';
import { apiRequest, apiAuthenticatedRequest, noUberTokenError } from '../utils/apiCall';
import { fetchUserFromSF } from '@ynap/user-state';
import { parse } from 'query-string';
import { fetchNavigation } from './fetchNavigation';
import { notificationActions } from '@ynap/notification-state';
import getConfig, { getCommerceConfig, getHeadConfig } from '../selectors/getConfig';
import getAvailabilityTabData from '../selectors/getAvailabilityTabData';
import { redirectToLogin } from '@ynap/redirect-utils';
import getPdpUrlTemplate from '../selectors/getPdpUrlTemplate';

const { PUBLIC, PRIVATE, BASE, ALL_ITEMS, INVENTORY } = views;

export function fetchWishlist(view, wishlistId, search, options = {}) {
    return async (dispatch, getState) => {
        const state = getState();
        const { baseUrl, brandId, clientId, country, locale, uberToken, langId } = getCommerceConfig(state);
        const {
            routes,
            availableImageSizes,
            manageWishlistFeatureToggle,
            orderByKeyDefault,
            availabilityTabDefault,
            inventory: {
                urlTemplates: { base: urlInventory },
            },
        } = getHeadConfig(state);
        const messages = getMessages(state);

        const wishlistURL = routes.base.replace(':locale', locale);
        const { pageNumber, orderBy, filterByAvailability, category } = parse(search);
        // TODO: API Should be returning us inventory items in recently added order. This is a quick fix while it doesn't.
        // 11 is the order by recently added query parameter key value
        let orderByDefault = view === INVENTORY ? 11 : orderByKeyDefault;

        let requestOptions = {
            itemPageNumber: pageNumber ? parseInt(pageNumber) : 1,
            itemPageSize: options.pageSize ? options.pageSize : 60,
            ...(langId && { langId }),
        };

        let getAllWishlistsOptions = {
            ...(langId && { langId }),
        };

        let selectedCategories = [];

        if (category) {
            const filteredCategories = typeof category === 'string' ? [category] : category;
            requestOptions['filterByCategoryId'] = filteredCategories.join(',');
            selectedCategories = filteredCategories;
        }
        if (orderBy || orderByDefault) requestOptions['sortBy'] = orderBy || orderByDefault;
        const selectedAvailability = getAvailabilityTabData(filterByAvailability || availabilityTabDefault).find((availability) => availability.selected);
        if (selectedAvailability.value) requestOptions.filterByAvailability = selectedAvailability.value;

        if (options.guestAccessKey) requestOptions['guestAccessKey'] = options.guestAccessKey;

        const pdpUrlTemplate = getPdpUrlTemplate(state);

        const config = getConfig(state);
        if (uberToken) {
            try {
                const response = await dispatch(
                    apiAuthenticatedRequest(
                        getRequest(view)({ baseUrl, brandId, country, uberToken, options: requestOptions, getAllWishlistsOptions, clientId, wishlistId }),
                        FETCH_DATA_PREFIX,
                        locale,
                        {
                            ...options,
                            messages,
                            locale,
                            availableImageSizes,
                            manageWishlistFeatureToggle,
                            orderBy,
                            selectedCategories,
                            pdpUrlTemplate,
                            config,
                        },
                    ),
                );
                const { json } = response;

                if (json.error) {
                    dispatch(handleError(json, wishlistURL, messages));
                }

                //If the view is BASE i.e. /wishlist, then response is list of available wishlists
                //Dispatch FETCH_NAVIGATION_PREFIX/SUCCESS with the response to add it to navigation state
                //If the view isn't BASE i.e. /wishlist/all, /wishlist/id ...etc then check if available wishlists is already in state
                //If not fetch navigation
                if (view === BASE) {
                    dispatch({ type: `${FETCH_NAVIGATION_PREFIX}/SUCCESS`, body: json, options: { locale } });
                } else {
                    const wishlists = state?.wishlistState?.navigation?.wishlistsList;
                    if (!wishlists || wishlists.length === 0) {
                        await dispatch(fetchNavigation());
                    }
                }

                dispatch({ type: SET_SELECTED_WISHLIST, selectedId: json?.GiftList?.[0]?.uniqueID });

                checkRedirect(json, view, wishlistURL, search, urlInventory, locale, dispatch);
            } catch (error) {
                dispatch(handleError(error, wishlistURL, messages));
            }
        } else {
            try {
                await dispatch(
                    apiRequest(
                        getRequest(view)({ baseUrl, brandId, country, options: requestOptions, getAllWishlistsOptions, clientId, wishlistId }),
                        FETCH_DATA_PREFIX,
                        {
                            ...options,
                            messages,
                            locale,
                            availableImageSizes,
                            manageWishlistFeatureToggle,
                            config,
                        },
                    ),
                );
            } catch (error) {
                dispatch(handleError(error, wishlistURL, messages));
            }
        }
    };
}

export function fetchData(view, wishlistId, search, options = {}) {
    return (dispatch, getState) => {
        const state = getState();
        const { locale, uberToken } = getCommerceConfig(state);

        const queryParameters = parse(search);
        options.sortBy = queryParameters.orderBy; // TODO: necessary to change the mocked sort options while we don't have them on the api
        if (view !== PUBLIC && !uberToken) {
            return dispatch(noUberTokenError(uberToken, FETCH_DATA_PREFIX, locale));
        }

        if (view === PUBLIC && !uberToken) {
            return dispatch(fetchWishlist(view, wishlistId, search, options));
        } else {
            return dispatch(fetchUserAndWishlist(view, wishlistId, search, options));
        }
    };
}

export function fetchUserAndWishlist(view, wishlistId, search, options = {}) {
    return (dispatch, getState) => {
        const state = getState();
        const { locale } = getCommerceConfig(state);
        const { defaultNumberOfEmptyProducts } = getHeadConfig(state);
        const userState = getUserState(state) || {};
        const forceLogin = true;

        if (!userState || !userState.hasOwnProperty('user') || userState.fetchUserError) {
            return dispatch(fetchUserFromSF(forceLogin))
                .then((response) => {
                    const user = response.body;
                    return dispatch(fetchWishlist(view, wishlistId, search, { user, defaultNumberOfEmptyProducts, ...options }));
                })
                .catch(() => {
                    return redirectToLogin(locale);
                });
        } else {
            const user = userState.user;
            return dispatch(fetchWishlist(view, wishlistId, search, { user, defaultNumberOfEmptyProducts, ...options }));
        }
    };
}

const checkRedirect = (data, view, wishlistURL, search = '', urlInventory, locale, dispatch) => {
    const ownsWishlist = isWishlistOwner(data, view);
    const moreThenOneWL = (data.hasOwnProperty('GiftList') && data.GiftList.length > 1) || false;
    if (view === BASE && ownsWishlist && moreThenOneWL) {
        return dispatch(replace(`${wishlistURL}/all`));
    } else if ((view === PUBLIC || view === BASE || view === PRIVATE) && ownsWishlist) {
        return dispatch(replace(`${wishlistURL}/${data.GiftList[0].uniqueID}${search}`));
    } else if (view === ALL_ITEMS) {
        return dispatch(replace(`${wishlistURL}/all${search}`));
    } else if (view === INVENTORY) {
        const inventory = urlInventory.replace(':locale', locale);
        return dispatch(replace(`${inventory}${search}`));
    }
};

const handleError = (error, wishlistURL, messages) => {
    return (dispatch) => {
        if (error.status === 400) {
            const errorMessage = error.error ? error.error.errors[0].errorKey : '';
            errorMessage &&
                dispatch(
                    notificationActions.setNotification({
                        name: 'action',
                        type: 'popup-error',
                        message: messages.notifications.FETCH_WISH_LIST_ERROR({ error: errorMessage }),
                    }),
                );
            return dispatch(push(`${wishlistURL}`));
        } else if (error.status >= 404 && error.status < 500) {
            // Does this still make sense or are we handling those errors elsewhere?
            return dispatch(push(`${wishlistURL}/404`));
        } else {
            // console.log(error); TODO: deal with 5xx error
        }
    };
};
